import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import "./Proposals.css";
import {
  IconAlerta,
  IconBusca,
  IconChevron,
  IconLista,
  IconProposta,
  IconRecarregar,
} from "../../Shared/Icons/Icons";
import ProposalDrawer from "./ProposalDrawer";
import StatusBadge from "./StatusBadge";
import { listarPropostas } from "./propostasApi";
import {
  STATUS,
  formatarData,
  formatarDataHora,
  formatarDocumento,
  formatarMoeda,
  rotuloCanal,
  tempoRelativo,
} from "./propostaFormato";

export const POR_PAGINA = 25;
const ESPERA_BUSCA_MS = 300;
const LINHAS_ESQUELETO = 5;

// Filtros, pagina e proposta aberta ficam na URL: da para recarregar e mandar o link.
function useFiltros() {
  const [params, setParams] = useSearchParams();

  const atualizar = useCallback(
    (mudancas, { manterPagina = false } = {}) => {
      setParams(
        (atuais) => {
          const novos = new URLSearchParams(atuais);
          Object.entries(mudancas).forEach(([chave, valor]) => {
            if (valor) novos.set(chave, String(valor));
            else novos.delete(chave);
          });
          if (!manterPagina) novos.delete("pagina");
          return novos;
        },
        { replace: true }
      );
    },
    [setParams]
  );

  return {
    status: params.get("status") || "",
    busca: params.get("busca") || "",
    pagina: Math.max(1, Number.parseInt(params.get("pagina"), 10) || 1),
    aberta: params.get("proposta"),
    atualizar,
  };
}

function FiltroStatus({ status, contagem, onMudar }) {
  const total = Object.values(contagem).reduce((soma, n) => soma + n, 0);
  const opcoes = [{ valor: "", rotulo: "Todas", quantidade: total }].concat(
    STATUS.map((item) => ({ ...item, quantidade: contagem[item.valor] || 0 }))
  );

  return (
    <div className="filtro-status" role="group" aria-label="Filtrar por status">
      {opcoes.map(({ valor, rotulo, quantidade }) => (
        <button
          key={valor || "todas"}
          type="button"
          className={`filtro-chip${valor ? ` filtro-chip--${valor}` : ""}`}
          aria-pressed={status === valor}
          onClick={() => onMudar(valor)}
        >
          {valor && <span className="status-badge-dot" aria-hidden="true" />}
          {rotulo}
          <span className="filtro-chip-total">{quantidade}</span>
        </button>
      ))}
    </div>
  );
}

function LinhasEsqueleto() {
  return Array.from({ length: LINHAS_ESQUELETO }, (_, indice) => (
    <tr key={indice} className="linha-esqueleto" aria-hidden="true">
      {Array.from({ length: 6 }, (__, coluna) => (
        <td key={coluna} className={coluna === 2 || coluna === 5 ? "col-secundaria" : undefined}>
          <span className="esqueleto" />
        </td>
      ))}
    </tr>
  ));
}

function TabelaPropostas({ propostas, carregandoInicial, onAbrir }) {
  return (
    <div className="table-wrapper">
      <table className="data-table propostas-tabela">
        <caption className="sr-only">
          Propostas comerciais, da atualização mais recente para a mais antiga
        </caption>
        <thead>
          <tr>
            <th scope="col">Proposta</th>
            <th scope="col">Cliente</th>
            <th scope="col" className="col-secundaria">
              Emissão
            </th>
            <th scope="col" className="col-numero">
              Valor
            </th>
            <th scope="col">Status</th>
            <th scope="col" className="col-secundaria">
              Atualizada
            </th>
          </tr>
        </thead>
        <tbody>
          {carregandoInicial ? (
            <LinhasEsqueleto />
          ) : (
            propostas.map((proposta) => (
              <tr
                key={proposta.numero}
                className="proposta-linha"
                onClick={(event) => onAbrir(proposta.numero, event.currentTarget)}
              >
                <td>
                  {/* O botao e o alvo de teclado; o clique na linha toda e atalho de mouse. */}
                  <button
                    type="button"
                    className="proposta-numero"
                    onClick={(event) => {
                      event.stopPropagation();
                      onAbrir(proposta.numero, event.currentTarget);
                    }}
                  >
                    Nº {proposta.numero}
                    <IconChevron size={14} />
                  </button>
                  {proposta.vendedor && (
                    <span className="celula-secundaria">{proposta.vendedor}</span>
                  )}
                </td>
                <td>
                  <span className="celula-principal">
                    {proposta.cliente?.razaoSocial || "Cliente não identificado"}
                  </span>
                  {proposta.cliente && (
                    <span className="celula-secundaria">
                      {formatarDocumento(proposta.cliente.documento)}
                    </span>
                  )}
                </td>
                <td className="col-secundaria">
                  {formatarData(proposta.dataEmissao)}
                  {proposta.prazoEntrega && (
                    <span className="celula-secundaria">
                      Entrega: {proposta.prazoEntrega}
                    </span>
                  )}
                </td>
                <td className="col-numero">{formatarMoeda(proposta.valorTotal)}</td>
                <td>
                  <StatusBadge status={proposta.status} />
                  {proposta.canalEnvio && (
                    <span className="celula-secundaria">
                      via {rotuloCanal(proposta.canalEnvio)}
                    </span>
                  )}
                </td>
                <td className="col-secundaria">
                  <time
                    dateTime={proposta.atualizadoEm}
                    title={formatarDataHora(proposta.atualizadoEm)}
                  >
                    {tempoRelativo(proposta.atualizadoEm)}
                  </time>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function Paginacao({ pagina, total, onMudar }) {
  const totalPaginas = Math.max(1, Math.ceil(total / POR_PAGINA));
  const inicio = (pagina - 1) * POR_PAGINA + 1;
  const fim = Math.min(pagina * POR_PAGINA, total);

  return (
    <nav className="paginacao" aria-label="Paginação">
      <p className="paginacao-texto">
        {inicio}–{fim} de {total} {total === 1 ? "proposta" : "propostas"}
      </p>
      {totalPaginas > 1 && (
        <div className="paginacao-botoes">
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={pagina <= 1}
            onClick={() => onMudar(pagina - 1)}
          >
            Anterior
          </button>
          <span className="paginacao-pagina">
            Página {pagina} de {totalPaginas}
          </span>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={pagina >= totalPaginas}
            onClick={() => onMudar(pagina + 1)}
          >
            Próxima
          </button>
        </div>
      )}
    </nav>
  );
}

export default function Proposals() {
  const { status, busca, pagina, aberta, atualizar } = useFiltros();
  const [textoBusca, setTextoBusca] = useState(busca);
  const [estado, setEstado] = useState({ carregando: true, erro: null, dados: null });
  const [versao, setVersao] = useState(0);
  const gatilhoRef = useRef(null);
  const buscaRef = useRef(null);

  // Busca com espera: so vai para a URL (e para a API) quando a pessoa para de digitar.
  useEffect(() => {
    if (textoBusca.trim() === busca) return undefined;
    const timer = setTimeout(
      () => atualizar({ busca: textoBusca.trim() }),
      ESPERA_BUSCA_MS
    );
    return () => clearTimeout(timer);
  }, [textoBusca, busca, atualizar]);

  useEffect(() => {
    const controle = new AbortController();
    setEstado((atual) => ({ ...atual, carregando: true, erro: null }));

    listarPropostas(
      { status, busca, pagina, limite: POR_PAGINA },
      { signal: controle.signal }
    )
      .then((dados) => setEstado({ carregando: false, erro: null, dados }))
      .catch((error) => {
        if (error.name === "AbortError") return;
        setEstado((atual) => ({ ...atual, carregando: false, erro: error }));
      });

    return () => controle.abort();
  }, [status, busca, pagina, versao]);

  const { carregando, erro, dados } = estado;
  const propostas = dados?.propostas ?? [];
  const total = dados?.total ?? 0;
  const temFiltro = Boolean(status || busca);

  // Pagina que ficou vazia (ex.: mudou o status da ultima da pagina): volta uma.
  useEffect(() => {
    if (!carregando && dados && propostas.length === 0 && pagina > 1) {
      atualizar({ pagina: pagina - 1 }, { manterPagina: true });
    }
  }, [carregando, dados, propostas.length, pagina, atualizar]);

  const abrir = useCallback(
    (numero, gatilho) => {
      gatilhoRef.current = gatilho;
      atualizar({ proposta: numero }, { manterPagina: true });
    },
    [atualizar]
  );

  const fechar = useCallback(() => {
    atualizar({ proposta: null }, { manterPagina: true });
    // Devolve o foco para quem abriu (o botao do numero, mesmo se clicou na linha).
    const gatilho = gatilhoRef.current;
    const alvo = gatilho?.matches?.("tr") ? gatilho.querySelector("button") : gatilho;
    requestAnimationFrame(() => alvo?.focus?.());
  }, [atualizar]);

  const recarregar = useCallback(() => setVersao((v) => v + 1), []);

  // A linha que abriu o drawer some da lista; o foco vai para a busca.
  const excluida = useCallback(() => {
    atualizar({ proposta: null }, { manterPagina: true });
    recarregar();
    requestAnimationFrame(() => buscaRef.current?.focus());
  }, [atualizar, recarregar]);

  const limparFiltros = () => {
    setTextoBusca("");
    atualizar({ status: null, busca: null });
  };

  const carregandoInicial = carregando && !dados;
  const vazio = !carregando && !erro && dados && propostas.length === 0;

  return (
    <div className="page">
      <div className="page-header page-header--acoes">
        <div>
          <h1 className="page-title">Propostas</h1>
          <p className="page-subtitle">
            Acompanhe as propostas capturadas do Tiny e atualize o status de cada uma.
          </p>
        </div>
        <Link to="/Proposta-Comercial" className="btn btn-primary">
          <IconProposta size={18} />
          Nova proposta
        </Link>
      </div>

      <section className="card propostas-card" aria-label="Lista de propostas">
        <div className="propostas-toolbar">
          <div className="busca">
            <label htmlFor="propostas-busca" className="sr-only">
              Buscar por número, cliente ou CNPJ/CPF
            </label>
            <IconBusca className="busca-icone" size={18} />
            <input
              ref={buscaRef}
              id="propostas-busca"
              type="search"
              className="field-input busca-input"
              placeholder="Buscar por número, cliente ou CNPJ/CPF"
              value={textoBusca}
              onChange={(e) => setTextoBusca(e.target.value)}
              autoComplete="off"
            />
          </div>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={recarregar}
            disabled={carregando}
          >
            <IconRecarregar size={16} className={carregando ? "girando" : undefined} />
            Atualizar
          </button>
        </div>

        <FiltroStatus
          status={status}
          contagem={dados?.contagemPorStatus ?? {}}
          onMudar={(valor) => atualizar({ status: valor })}
        />

        {/* Anuncia o resultado para leitor de tela sem roubar o foco. */}
        <p className="sr-only" role="status" aria-live="polite">
          {carregando
            ? "Carregando propostas…"
            : dados
              ? `${total} ${total === 1 ? "proposta encontrada" : "propostas encontradas"}`
              : ""}
        </p>

        {erro && (
          <div className="alert alert-error" role="alert">
            <IconAlerta size={18} />
            <div className="alert-conteudo">
              <span>{erro.message}</span>
              <button type="button" className="btn btn-secondary btn-sm" onClick={recarregar}>
                <IconRecarregar size={16} />
                Tentar novamente
              </button>
            </div>
          </div>
        )}

        {vazio && !temFiltro && (
          <div className="empty-state">
            <IconLista size={28} />
            <p className="empty-state-title">Nenhuma proposta capturada ainda</p>
            <p className="empty-state-text">
              Gere o PDF de uma proposta com “Salvar dados” marcado e ela aparece aqui.
            </p>
            <Link to="/Proposta-Comercial" className="btn btn-secondary btn-sm">
              Nova proposta
            </Link>
          </div>
        )}

        {vazio && temFiltro && (
          <div className="empty-state">
            <IconBusca size={28} />
            <p className="empty-state-title">Nenhuma proposta encontrada</p>
            <p className="empty-state-text">Tente outro termo ou outro status.</p>
            <button type="button" className="btn btn-secondary btn-sm" onClick={limparFiltros}>
              Limpar filtros
            </button>
          </div>
        )}

        {(carregandoInicial || propostas.length > 0) && (
          <div className={carregando && !carregandoInicial ? "propostas-atualizando" : undefined}>
            <TabelaPropostas
              propostas={propostas}
              carregandoInicial={carregandoInicial}
              onAbrir={abrir}
            />
          </div>
        )}

        {propostas.length > 0 && (
          <Paginacao
            pagina={pagina}
            total={total}
            onMudar={(nova) => atualizar({ pagina: nova }, { manterPagina: true })}
          />
        )}
      </section>

      {aberta && (
        <ProposalDrawer
          numero={aberta}
          onFechar={fechar}
          onAlterada={recarregar}
          onExcluida={excluida}
        />
      )}
    </div>
  );
}
